import { Component } from '@theme/component';

/**
 * <particle-logo>
 *
 * Canvas-only enhancement for the landing hero's wordmark, adapted from the
 * prototype's `apps/full-bleed/src/particle-logo.js`. The static
 * `<img class="ph-landing__logo">` stays in the DOM as the accessible and
 * failure-safe source; this component only replaces its presentation, and
 * `.ph-landing.particle-logo-ready` fades the image out once the canvas is
 * actually drawing.
 *
 * Expected markup (see sections/landing-hero.liquid):
 *
 *   <div class="ph-landing" data-ph-panel data-ph-landing ...>
 *     <img class="ph-landing__logo" crossorigin="anonymous" ...>
 *     <particle-logo class="ph-landing__particles" aria-hidden="true">
 *       <canvas ref="canvas"></canvas>
 *     </particle-logo>
 *   </div>
 *
 * The wordmark's alpha channel is sampled with `getImageData`, so the source
 * image must be CORS-clean — hence `crossorigin="anonymous"` on the <img>,
 * which works for both `asset_url` and `image_url` because Shopify's CDN
 * answers with `Access-Control-Allow-Origin: *`.
 *
 * Gating: the effect never starts under `prefers-reduced-motion: reduce`,
 * pauses when the panel scrolls out of the showcase, and pauses when the tab
 * is hidden. It runs on touch devices too (client request — the prototype
 * skipped them): a finger dragged across the wordmark disturbs the particles
 * the same way the cursor does, and lifting it lets them settle, while small
 * screens use the lighter "compact" particle count (COMPACT_QUERY).
 * Everything is torn down in `disconnectedCallback`, so a theme-editor
 * section reload leaves nothing running.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const COMPACT_QUERY = '(max-width: 760px), (hover: none) and (pointer: coarse)';

const DUST_MAX = 220;
const MAX_DPR = 2;
const ALPHA_THRESHOLD = 48;

class ParticleLogo extends Component {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {HTMLElement | null} */
  #landing = null;
  /** @type {HTMLImageElement | null} */
  #logo = null;
  /** @type {HTMLCanvasElement | null} */
  #canvas = null;
  /** @type {CanvasRenderingContext2D | null} */
  #context = null;
  /** @type {MediaQueryList | null} */
  #compact = null;

  #particles = [];
  #dust = [];
  #frame = 0;
  #rebuildTimer = 0;
  #visible = true;
  #started = false;
  #startedAt = 0;
  #dpr = 1;
  #width = 0;
  #height = 0;
  #originX = 0;
  #originY = 0;
  #baseColor = '#ffffff';
  #starColor = '#ffd7ef';

  #pointer = {
    x: 0,
    y: 0,
    inside: false,
    speed: 0,
    vx: 0,
    vy: 0,
    lastX: 0,
    lastY: 0,
    lastAt: 0,
    hasPosition: false,
  };

  connectedCallback() {
    super.connectedCallback();

    if (matchMedia(REDUCED_MOTION_QUERY).matches) return;

    void this.#start();
  }

  disconnectedCallback() {
    this.#stop();
    super.disconnectedCallback();
  }

  async #start() {
    try {
      const canvas = /** @type {HTMLCanvasElement | null} */ (
        this.refs.canvas instanceof HTMLCanvasElement ? this.refs.canvas : this.querySelector('canvas')
      );
      const landing = /** @type {HTMLElement | null} */ (this.closest('.ph-landing'));
      const logo = /** @type {HTMLImageElement | null} */ (landing?.querySelector('.ph-landing__logo') ?? null);

      if (!canvas || !landing || !logo) return;

      const context = canvas.getContext('2d', { alpha: true });
      if (!context) return;

      if (!logo.complete) await logo.decode();
      // The element can be pulled out again while the image decodes.
      if (!this.isConnected) return;

      this.#canvas = canvas;
      this.#context = context;
      this.#landing = landing;
      this.#logo = logo;
      this.#compact = matchMedia(COMPACT_QUERY);
      this.#started = true;

      // Particle colors are theme tokens, resolved to concrete rgb() values
      // through the computed `color` of the host and the canvas (see
      // component-landing-hero.css) — canvas fillStyle cannot read a custom
      // property or a color-mix() token stream directly.
      this.#baseColor = getComputedStyle(this).color || this.#baseColor;
      this.#starColor = getComputedStyle(canvas).color || this.#starColor;

      const controller = new AbortController();
      this.#controller = controller;
      const { signal } = controller;

      window.addEventListener('pointermove', this.#handlePointerMove, { passive: true, signal });
      document.documentElement.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
      // A finger has no hover: once it lifts (or the browser takes the
      // gesture over for scrolling), treat it like the cursor leaving.
      window.addEventListener('pointerup', this.#handleTouchEnd, { passive: true, signal });
      window.addEventListener('pointercancel', this.#handleTouchEnd, { passive: true, signal });
      document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });

      this.#intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.#visible = Boolean(entry?.isIntersecting);
          if (this.#visible) this.#requestDraw();
          else this.#cancelDraw();
        },
        { root: this.closest('[data-ph-showcase]'), threshold: 0 }
      );
      this.#intersectionObserver.observe(landing);

      this.#resizeObserver = new ResizeObserver(this.#scheduleBuild);
      this.#resizeObserver.observe(landing);

      this.#build();
    } catch (error) {
      this.#stop();
      console.warn('Particle logo unavailable; showing the static wordmark.', error);
    }
  }

  #stop() {
    this.#cancelDraw();
    if (this.#rebuildTimer) {
      clearTimeout(this.#rebuildTimer);
      this.#rebuildTimer = 0;
    }
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#controller?.abort();
    this.#controller = null;
    this.#landing?.classList.remove('particle-logo-ready');
    this.#particles = [];
    this.#dust = [];
    this.#started = false;
  }

  #scheduleBuild = () => {
    if (!this.#started) return;
    if (this.#rebuildTimer) clearTimeout(this.#rebuildTimer);
    this.#rebuildTimer = setTimeout(() => {
      this.#rebuildTimer = 0;
      this.#build();
    }, 150);
  };

  /**
   * Samples the wordmark's alpha channel on an offscreen canvas and turns
   * every opaque sample point into a particle, each starting at a random
   * offset from its home so the wordmark converges into place.
   */
  #build() {
    const canvas = this.#canvas;
    const context = this.#context;
    const logo = this.#logo;
    const landing = this.#landing;
    if (!canvas || !context || !logo || !landing) return;

    const hostRect = this.getBoundingClientRect();
    const logoRect = logo.getBoundingClientRect();
    if (!hostRect.width || !hostRect.height || !logoRect.width || !logoRect.height) return;

    const dpr = Math.min(devicePixelRatio || 1, MAX_DPR);
    this.#dpr = dpr;
    this.#width = hostRect.width;
    this.#height = hostRect.height;
    canvas.width = Math.round(hostRect.width * dpr);
    canvas.height = Math.round(hostRect.height * dpr);
    context.setTransform(dpr, 0, 0, dpr, 0, 0);

    const mask = document.createElement('canvas');
    const maskWidth = Math.max(1, Math.round(logoRect.width));
    const maskHeight = Math.max(1, Math.round(logoRect.height));
    mask.width = maskWidth;
    mask.height = maskHeight;
    const maskContext = mask.getContext('2d', { willReadFrequently: true });
    if (!maskContext) return;
    maskContext.drawImage(logo, 0, 0, maskWidth, maskHeight);
    const pixels = maskContext.getImageData(0, 0, maskWidth, maskHeight).data;

    const compact = Boolean(this.#compact?.matches);
    const gap = compact ? 4 : 5;
    const points = [];
    for (let y = 0; y < maskHeight; y += gap) {
      for (let x = 0; x < maskWidth; x += gap) {
        if (pixels[(y * maskWidth + x) * 4 + 3] > ALPHA_THRESHOLD) points.push([x, y]);
      }
    }
    points.sort(() => Math.random() - 0.5);
    points.length = Math.min(points.length, compact ? 900 : 1600);

    const offsetX = logoRect.left - hostRect.left;
    const offsetY = logoRect.top - hostRect.top;
    this.#particles = points.map(([x, y], index) => {
      const homeX = offsetX + x;
      const homeY = offsetY + y;
      const angle = Math.random() * Math.PI * 2;
      const distance = 35 + Math.random() * 125;
      return {
        homeX,
        homeY,
        startX: homeX + Math.cos(angle) * distance,
        startY: homeY + Math.sin(angle) * distance,
        size: 1.1 + Math.random() * 2.2,
        phase: Math.random() * Math.PI * 2,
        freq: 0.05 + Math.random() * 0.09,
        delay: Math.random() * 500,
        star: index % 13 === 0,
        offsetX: 0,
        offsetY: 0,
      };
    });

    if (!this.#particles.length) return;

    this.#startedAt = performance.now();
    landing.classList.add('particle-logo-ready');
    this.#requestDraw();
  }

  /**
   * Dust flies free and fades instead of snapping back, keeping the cursor
   * response spark-like rather than turning the wordmark into a fluid
   * surface. Fixed-size array via shift(), not a pool; fine at this count.
   */
  #spawnDust(x, y, vx, vy, now) {
    const dust = this.#dust;
    if (dust.length >= DUST_MAX) dust.shift();
    dust.push({
      x,
      y,
      vx,
      vy,
      bornAt: now,
      life: 480 + Math.random() * 420,
      size: 0.9 + Math.random() * 1.8,
      star: Math.random() < 0.3,
      seed: Math.random() * Math.PI * 2,
    });
  }

  #draw = (now = performance.now()) => {
    this.#frame = 0;
    const context = this.#context;
    if (!context) return;

    // The panel slides horizontally inside the showcase, so the pointer's
    // viewport coordinates are re-based against the canvas every frame
    // rather than compared to canvas-local particle homes directly.
    const hostRect = this.getBoundingClientRect();
    this.#originX = hostRect.left;
    this.#originY = hostRect.top;

    const width = this.#width;
    const height = this.#height;
    context.clearRect(0, 0, width, height);

    const pointer = this.#pointer;
    const pointerX = pointer.x - this.#originX;
    const pointerY = pointer.y - this.#originY;
    pointer.speed *= 0.9;
    if (pointer.speed < 0.005) pointer.speed = 0;
    pointer.vx *= 0.88;
    pointer.vy *= 0.88;

    const compact = Boolean(this.#compact?.matches);

    this.#particles.forEach((particle) => {
      const raw = Math.max(0, Math.min(1, (now - this.#startedAt - particle.delay) / 1600));
      const progress = 1 - (1 - raw) ** 4;
      let targetX = 0;
      let targetY = 0;
      let interaction = 0;

      if (pointer.inside && pointer.speed > 0) {
        // Per-particle displacement keeps the response local to the cursor.
        const dx = particle.homeX - pointerX;
        const dy = particle.homeY - pointerY;
        const distance = Math.hypot(dx, dy);
        const interactionRadius = (compact ? 85 : 115) * (0.6 + pointer.speed * 0.4);
        if (distance > 0 && distance < interactionRadius) {
          const falloff = (1 - distance / interactionRadius) ** 2;
          interaction = falloff * pointer.speed;
          targetX = (pointer.vx * 0.8 + (dx / distance) * 5) * falloff;
          targetY = (pointer.vy * 0.8 + (dy / distance) * 5) * falloff;
          if (Math.random() < falloff * pointer.speed * 0.4) {
            const spreadX = (Math.random() - 0.5) * 3.5;
            const spreadY = (Math.random() - 0.5) * 3.5;
            this.#spawnDust(
              particle.homeX + particle.offsetX,
              particle.homeY + particle.offsetY,
              pointer.vx * 1.1 + (dx / distance) * 2.2 + spreadX,
              pointer.vy * 1.1 + (dy / distance) * 2.2 + spreadY,
              now
            );
          }
        }
      }

      particle.offsetX += (targetX - particle.offsetX) * 0.5;
      particle.offsetY += (targetY - particle.offsetY) * 0.5;
      const x = particle.startX + (particle.homeX - particle.startX) * progress + particle.offsetX;
      const y = particle.startY + (particle.homeY - particle.startY) * progress + particle.offsetY;
      const baseSparkle = Math.max(0, Math.sin(now * 0.006 + particle.phase));
      const interactionSparkle = interaction * Math.max(0, Math.sin(now * particle.freq * 3 + particle.phase * 4));
      const sparkle = Math.max(baseSparkle, interactionSparkle);
      const radius = particle.size * (0.8 + sparkle * 0.55 + interaction * 1.3) * progress;

      context.globalAlpha = (0.62 + sparkle * 0.38) * progress;
      context.fillStyle = particle.star ? this.#starColor : this.#baseColor;
      context.beginPath();
      context.arc(x, y, radius, 0, Math.PI * 2);
      context.fill();

      if ((particle.star || interaction > 0.2) && sparkle > 0.55) {
        const ray = radius * (4 + interaction * 2.5);
        context.strokeStyle = this.#baseColor;
        context.lineWidth = 0.7 + interaction * 0.6;
        context.beginPath();
        context.moveTo(x - ray, y);
        context.lineTo(x + ray, y);
        context.moveTo(x, y - ray);
        context.lineTo(x, y + ray);
        context.stroke();
      }
    });

    context.globalAlpha = 1;

    for (let index = this.#dust.length - 1; index >= 0; index -= 1) {
      const speck = this.#dust[index];
      const age = now - speck.bornAt;
      if (age > speck.life) {
        this.#dust.splice(index, 1);
        continue;
      }
      speck.x += speck.vx;
      speck.y += speck.vy;
      speck.vx *= 0.94;
      speck.vy *= 0.94;
      const lifeProgress = age / speck.life;
      const fade = (1 - lifeProgress) ** 1.4;
      const twinkle = Math.max(0, Math.sin(now * 0.03 + speck.seed));
      const radius = speck.size * (0.7 + twinkle * 0.6);
      context.globalAlpha = fade * (0.55 + twinkle * 0.45);
      context.fillStyle = speck.star ? this.#starColor : this.#baseColor;
      context.beginPath();
      context.arc(speck.x, speck.y, radius, 0, Math.PI * 2);
      context.fill();
      if (speck.star && twinkle > 0.6) {
        const ray = radius * 4;
        context.strokeStyle = this.#baseColor;
        context.lineWidth = 0.7;
        context.beginPath();
        context.moveTo(speck.x - ray, speck.y);
        context.lineTo(speck.x + ray, speck.y);
        context.moveTo(speck.x, speck.y - ray);
        context.lineTo(speck.x, speck.y + ray);
        context.stroke();
      }
    }
    context.globalAlpha = 1;

    if (this.#visible && !document.hidden && this.isConnected) {
      this.#frame = requestAnimationFrame(this.#draw);
    }
  };

  #requestDraw() {
    if (this.#frame || !this.#started || !this.#visible || document.hidden) return;
    this.#frame = requestAnimationFrame(this.#draw);
  }

  #cancelDraw() {
    if (!this.#frame) return;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #handleTouchEnd = (event) => {
    if (event.pointerType === 'touch') this.#handlePointerLeave();
  };

  #handlePointerMove = (event) => {
    const pointer = this.#pointer;
    const x = event.clientX;
    const y = event.clientY;
    const now = performance.now();
    if (pointer.hasPosition) {
      const elapsed = Math.max(8, now - pointer.lastAt);
      pointer.vx = ((x - pointer.lastX) / elapsed) * 16;
      pointer.vy = ((y - pointer.lastY) / elapsed) * 16;
      const measured = Math.min(1, Math.hypot(x - pointer.lastX, y - pointer.lastY) / elapsed / 1.2);
      pointer.speed += (measured - pointer.speed) * 0.45;
    } else {
      pointer.hasPosition = true;
    }
    pointer.x = pointer.lastX = x;
    pointer.y = pointer.lastY = y;
    pointer.lastAt = now;
    pointer.inside = true;
    this.#requestDraw();
  };

  #handlePointerLeave = () => {
    const pointer = this.#pointer;
    pointer.inside = false;
    pointer.speed = 0;
    pointer.vx = 0;
    pointer.vy = 0;
    pointer.hasPosition = false;
    this.#requestDraw();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) this.#cancelDraw();
    else this.#requestDraw();
  };
}

if (!customElements.get('particle-logo')) {
  customElements.define('particle-logo', ParticleLogo);
}
